/**
 * The open questions Wingo asks (XC-PRICE-GUARD U3b §5): `GET /api/v1/business-profile/questions`
 * (gateway → entity_resolution, `OpenQuestionsOut`), one card per ITEM whose held prices wait on a
 * unit answer. The read is shared with the chat's `list_open_questions`
 * (`askwingo_shared.price_unit_questions`).
 *
 * A held price is one the price guard set aside from the item's price history because it sits far
 * from the price in force (a figure per bag beside a history per pound reads as a jump): the item
 * keeps its earlier price until Dub says which unit the figure is in. `total_items` is the true
 * count (AW-51); `items` may stop short (`truncated`). Prices are decimal TEXT exactly as the row
 * carries them (`180`, `0.00645`), never floats.
 *
 * Ids (`material_id`, `observation_id`, `document_id`, `thread_id`), `ruling_refs` and `questions`
 * are for keys and for the answer only, never printed. `questions` names each question the card
 * groups (its chain, the tip an answer cites as `based_on`, its party) and each price the question it
 * came from (`ruling_ref`), so the Inbox's answer (XC-PRICE-GUARD U6b part 1) files on exactly the
 * questions the card showed. Objects are not strict: a field the engine adds later must not blank
 * the Inbox.
 */

import { z } from 'zod';

const decimalText = z.string().regex(/^-?\d+(\.\d+)?$/);

export const QuestionPriceSchema = z.object({
  ruling_ref: z.string().nullable().optional(),
  observation_id: z.string().uuid(),
  effective_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  as_observed: decimalText,
  per_unit: decimalText,
  in_force: decimalText,
  fold: decimalText.nullable().optional(),
  document_id: z.string().uuid().nullable().optional(),
  document_name: z.string().nullable().optional(),
});

export const QuestionThreadSchema = z.object({
  thread_id: z.string().min(1),
  title: z.string().nullable().optional(),
});

export const QuestionChainSchema = z.object({
  ruling_ref: z.string().min(1),
  based_on: z.string().uuid(),
  party: z.string().nullable().optional(),
});

export const ItemQuestionSchema = z.object({
  material_id: z.string().uuid(),
  item_name: z.string().min(1),
  party: z.string().nullable().optional(),
  price_words: z.array(z.string()).default([]),
  shapes: z.number().int().nonnegative(),
  held_total: z.number().int().nonnegative(),
  prices: z.array(QuestionPriceSchema),
  prices_truncated: z.boolean().default(false),
  threads: z.array(QuestionThreadSchema).default([]),
  ruling_refs: z.array(z.string()),
  questions: z.array(QuestionChainSchema).default([]),
  asked_at: z.string().datetime({ offset: true }),
  answer_available: z.boolean().default(false),
  answer_reason: z.string().nullable().optional(),
});

export const OpenQuestionsSchema = z.object({
  total_items: z.number().int().nonnegative(),
  items: z.array(ItemQuestionSchema),
  truncated: z.boolean().default(false),
});

export type QuestionPrice = z.infer<typeof QuestionPriceSchema>;
export type QuestionThread = z.infer<typeof QuestionThreadSchema>;
export type QuestionChain = z.infer<typeof QuestionChainSchema>;
export type ItemQuestion = z.infer<typeof ItemQuestionSchema>;
export type OpenQuestions = z.infer<typeof OpenQuestionsSchema>;
